import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { useLang } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: "", company: "", email: "", phone: "", country: "", interest: "regular", message: "" };
const inputCls = "w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-[15px] text-white outline-none transition-[border-color,box-shadow] focus:border-[#2F7BFF] focus:ring-4 focus:ring-[#2F7BFF]/20";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-sm font-semibold text-white">{label}</span>
    {children}
  </label>
);

export const ContactForm = ({ vehicle = null }) => {
  const { t, lang } = useLang();
  const c = t.contact;
  const [form, setForm] = useState(() => (vehicle ? { ...EMPTY, interest: "vehicle", message: t.vehicles.requestMessage } : EMPTY));
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (![form.name, form.company, form.email, form.message].every((v) => v.trim())) return toast.error(c.required);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return toast.error(c.invalidEmail);
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, { ...form, phone: form.phone || null, country: form.country || null, language: lang, vehicle_id: vehicle?.id || null });
      setDone(true);
      setForm(EMPTY);
      toast.success(c.success);
    } catch {
      toast.error(c.error);
    } finally {
      setLoading(false);
    }
  };

  if (done) return (
    <div data-testid="contact-success" className="flex min-h-[420px] flex-col items-center justify-center text-center">
      <CheckCircle2 className="h-16 w-16 text-emerald-500" />
      <p className="mt-6 max-w-sm text-lg font-semibold text-white">{c.success}</p>
      {!vehicle && <button data-testid="contact-send-another" onClick={() => setDone(false)} className="mt-6 text-sm font-bold text-[#2F7BFF] hover:underline">↺ {c.formTitle}</button>}
    </div>
  );

  return (
    <form data-testid="contact-form" onSubmit={submit} className="space-y-4" noValidate>
      {!vehicle && <h3 className="font-display text-2xl font-bold text-white">{c.formTitle}</h3>}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={`${c.fields.name} *`}><input data-testid="contact-name-input" className={inputCls} value={form.name} onChange={set("name")} /></Field>
        <Field label={`${c.fields.company} *`}><input data-testid="contact-company-input" className={inputCls} value={form.company} onChange={set("company")} /></Field>
        <Field label={`${c.fields.email} *`}><input data-testid="contact-email-input" type="email" className={inputCls} value={form.email} onChange={set("email")} /></Field>
        <Field label={c.fields.phone}><input data-testid="contact-phone-input" className={inputCls} value={form.phone} onChange={set("phone")} /></Field>
        <Field label={c.fields.country}><input data-testid="contact-country-input" className={inputCls} value={form.country} onChange={set("country")} /></Field>
        {!vehicle && <Field label={c.fields.interest}>
          <select data-testid="contact-interest-select" className={`${inputCls} [&>option]:bg-[#0B1224]`} value={form.interest} onChange={set("interest")}>
            {Object.entries(c.interests).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </Field>}
      </div>
      <Field label={`${c.fields.message} *`}><textarea data-testid="contact-message-input" rows={5} className={`${inputCls} resize-none`} value={form.message} onChange={set("message")} /></Field>
      <button data-testid="contact-submit-button" disabled={loading} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#2F7BFF] px-8 py-4 text-base font-bold text-white transition-colors hover:bg-[#1F63E0] disabled:opacity-70 sm:w-auto">
        {loading ? <><Loader2 className="h-5 w-5 animate-spin" />{c.sending}</> : <><Send className="h-4 w-4" />{c.submit}</>}
      </button>
    </form>
  );
};
